import { useEffect, useState } from 'react';
import { useSectionRoster, useCreateStudentGroup, useUpdateStudentGroup } from '../../../api/hooks';
import type { StudentGroup, StudentGroupWriteBody } from '../../../api/types';
import Modal from '../../../components/ui/Modal';
import FormField from '../../../components/ui/FormField';
import Button from '../../../components/ui/Button';
import { personName } from '../../../lib/names';

interface Props {
  sectionId: number;
  seed?: { name: string; member_ids: number[] };
  existing?: StudentGroup;
  onClose: () => void;
  /** Extra fields sent on create (teacher grouping: subject_id, lens, criteria). */
  extra?: Partial<Omit<StudentGroupWriteBody, 'name' | 'notes' | 'member_ids'>>;
  /** Dialog title for a new group (default "New group"). */
  title?: string;
  onSaved?: () => void;
}

export default function StudentGroupForm({ sectionId, seed, existing, onClose, extra, title, onSaved }: Props) {
  const rosterQ = useSectionRoster(sectionId);
  const createMut = useCreateStudentGroup(sectionId);
  const updateMut = useUpdateStudentGroup(sectionId, existing?.id ?? 0);

  const initialName = existing?.name ?? seed?.name ?? '';
  const initialNotes = existing?.notes ?? '';
  const initialMemberIds = existing?.members.map((m) => m.id) ?? seed?.member_ids ?? [];

  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState(initialNotes);
  const [selected, setSelected] = useState<Set<number>>(new Set(initialMemberIds));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialName);
    setNotes(initialNotes);
    setSelected(new Set(initialMemberIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, seed?.name]);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function submit(e?: React.FormEvent | React.MouseEvent) {
    e?.preventDefault();
    setError(null);
    const body = {
      name: name.trim(),
      notes: notes.trim(),
      member_ids: Array.from(selected),
    };
    if (!body.name) {
      setError('Name is required.');
      return;
    }
    try {
      if (existing) {
        await updateMut.mutateAsync(body);
      } else {
        await createMut.mutateAsync({ ...extra, ...body });
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      const nameErr = Array.isArray(data?.name) ? data.name[0] : data?.name;
      setError(nameErr ?? data?.detail ?? err?.message ?? 'Save failed.');
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={existing ? 'Edit group' : (title ?? 'New group')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button
            variant="primary"
            onClick={(e) => submit(e)}
            loading={createMut.isPending || updateMut.isPending}
            type="button">
            {existing ? 'Save' : 'Create'}
          </Button>
        </>
      )}>
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Name" htmlFor="grp-name" required error={error ?? undefined}>
          <input
            id="grp-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-h-[40px] w-full rounded-[12px] border border-line-strong bg-surface px-3 py-1.5 text-sm text-ink" />
        </FormField>
        <FormField label="Notes" htmlFor="grp-notes" hint="Optional">
          <textarea
            id="grp-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="min-h-[40px] w-full rounded-[12px] border border-line-strong bg-surface px-3 py-1.5 text-sm text-ink" />
        </FormField>
        <div className="space-y-1">
          <span className="block text-xs font-semibold text-muted">Members</span>
          <div className="max-h-48 overflow-y-auto rounded-[12px] border border-line-strong p-2">
            {rosterQ.isLoading ? (
              <p className="text-xs text-muted">Loading roster…</p>
            ) : rosterQ.isError || !rosterQ.data ? (
              <p className="text-xs text-danger">Failed to load roster.</p>
            ) : rosterQ.data.students.length === 0 ? (
              <p className="text-xs text-muted">No students in section.</p>
            ) : (
              rosterQ.data.students.map((s) => (
                <label key={s.id} className="flex min-h-[40px] items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  <span>{personName(s)}</span>
                </label>
              ))
            )}
          </div>
          <div className="text-xs text-muted">{selected.size} selected</div>
        </div>
      </form>
    </Modal>
  );
}
